import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { purchaseOptimizerService } from '@/modules/purchase-optimizer/services'
import type { OptimizationResult } from '@/modules/purchase-optimizer/types'
import { toastError, toastSuccess } from '@/stores'
import { usePurchaseOptimizerStore } from '@/stores/purchase-optimizer-store'
import { useQuoteDraftStore } from '@/stores/quote-draft-store'

const QUERY_KEY = ['purchase-optimizer'] as const

export function usePurchaseOptimizer() {
  const queryClient = useQueryClient()
  const draftItems = useQuoteDraftStore((s) => s.items)
  const clearDraft = useQuoteDraftStore((s) => s.clear)
  const addFromProduct = useQuoteDraftStore((s) => s.addFromProduct)

  const hydrate = usePurchaseOptimizerStore((s) => s.hydrate)
  const ready = usePurchaseOptimizerStore((s) => s.ready)
  const strategy = usePurchaseOptimizerStore((s) => s.strategy)
  const demands = usePurchaseOptimizerStore((s) => s.demands)
  const result = usePurchaseOptimizerStore((s) => s.result)
  const comparison = usePurchaseOptimizerStore((s) => s.comparison)
  const offersCount = usePurchaseOptimizerStore((s) => s.offersCount)
  const history = usePurchaseOptimizerStore((s) => s.history)
  const weights = usePurchaseOptimizerStore((s) => s.weights)
  const lastError = usePurchaseOptimizerStore((s) => s.lastError)
  const setStrategy = usePurchaseOptimizerStore((s) => s.setStrategy)
  const loadFromDraft = usePurchaseOptimizerStore((s) => s.loadFromDraft)
  const loadSample = usePurchaseOptimizerStore((s) => s.loadSample)
  const runOptimize = usePurchaseOptimizerStore((s) => s.runOptimize)
  const saveWeights = usePurchaseOptimizerStore((s) => s.saveWeights)

  const bootstrap = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      hydrate()
      loadFromDraft(useQuoteDraftStore.getState().items)
      return usePurchaseOptimizerStore.getState().demands.length
    },
  })

  useEffect(() => {
    if (!ready) hydrate()
  }, [ready, hydrate])

  const optimize = useCallback(() => {
    runOptimize()
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    const error = usePurchaseOptimizerStore.getState().lastError
    const next = usePurchaseOptimizerStore.getState().result
    if (error) toastError('Otimização', error)
    else if (next) toastSuccess('Otimização concluída', `Economia R$ ${next.economy.toFixed(2)}`)
  }, [runOptimize, queryClient])

  const recalculate = useCallback(() => {
    loadFromDraft(useQuoteDraftStore.getState().items)
    runOptimize()
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    toastSuccess('Recalculado', 'Plano atualizado com o orçamento atual')
  }, [loadFromDraft, runOptimize, queryClient])

  const applyToQuote = useCallback(() => {
    const plan = usePurchaseOptimizerStore.getState().result
    if (!plan) {
      toastError('Aplicar', 'Execute a otimização antes')
      return
    }
    clearDraft()
    for (const item of plan.items) {
      addFromProduct({
        productId: item.productId,
        codigoInterno: item.sku,
        descricao: `${item.product} · ${item.supplier}`,
        valorManual: item.unitPrice,
        quantity: item.quantity,
        source: 'search',
      })
    }
    loadFromDraft(useQuoteDraftStore.getState().items)
    toastSuccess('Aplicado ao orçamento', `${plan.items.length} itens atualizados`)
  }, [clearDraft, addFromProduct, loadFromDraft])

  const exportExcel = useCallback(() => {
    const plan = usePurchaseOptimizerStore.getState().result
    if (!plan) {
      toastError('Exportar', 'Sem resultado para exportar')
      return
    }
    downloadText(purchaseOptimizerService.exportCsv(plan), 'otimizacao-compras.csv')
    toastSuccess('Excel/CSV', 'Arquivo gerado')
  }, [])

  const exportPdf = useCallback(() => {
    const plan = usePurchaseOptimizerStore.getState().result
    if (!plan) {
      toastError('Exportar', 'Sem resultado para exportar')
      return
    }
    const meta = purchaseOptimizerService.exportPdfMeta(plan)
    toastSuccess('PDF', meta.message)
  }, [])

  const syncDraft = useCallback(() => {
    loadFromDraft(draftItems)
  }, [draftItems, loadFromDraft])

  return {
    ready,
    strategy,
    setStrategy,
    demands,
    result,
    comparison,
    offersCount,
    history,
    weights,
    saveWeights,
    lastError,
    draftCount: draftItems.length,
    optimize,
    recalculate,
    applyToQuote,
    exportExcel,
    exportPdf,
    loadSample,
    syncDraft,
    isLoading: bootstrap.isLoading,
    distribution: buildDistribution(result),
  }
}

function buildDistribution(result: OptimizationResult | null) {
  if (!result) return [] as Array<{ supplier: string; value: number }>
  return result.freightBySupplier
    .map((row) => ({
      supplier: row.supplier,
      value: round2(row.productCost + row.freight),
    }))
    .sort((a, b) => b.value - a.value)
}

function downloadText(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}
